import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
} from "@mui/material";
import useAuth from "../../hooks/useAuth";
import AuthService from "../../api/axiosInstance";
import endpoints from "../../api/endpoints";

export default function LoginDialog({ open, onClose }) {
  const { login } = useAuth();
  const [mode, setMode] = useState("login"); // login | reset
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState(null);
  const [errorKind, setErrorKind] = useState(null);
  const [resetEmail, setResetEmail] = useState("");
  const [resetMessage, setResetMessage] = useState(null);
  const [resetSubmitted, setResetSubmitted] = useState(false);
  const [resendMessage, setResendMessage] = useState(null);
  const [resendSending, setResendSending] = useState(false);

  const resetState = () => {
    setMode("login");
    setForm({ email: "", password: "" });
    setError(null);
    setErrorKind(null);
    setResetEmail("");
    setResetMessage(null);
    setResetSubmitted(false);
    setResendMessage(null);
    setResendSending(false);
  };

  const handleChange = (e) => {
    if (e.target.name === "email") {
      setResendMessage(null);
    }
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (mode === "login") {
      try {
        await login(form);
        resetState();
        onClose();
      } catch (err) {
        const detail =
          err?.response?.data?.detail ||
          "Invalid email or password. If you recently registered, please verify your email.";
        const isUnverified =
          typeof detail === "string" && detail.toLowerCase().includes("verify your email to continue");
        setError(
          isUnverified
            ? "Please verify your email to continue. Resend confirmation email?"
            : detail
        );
        setErrorKind(isUnverified ? "unverified" : null);
        setResendMessage(null);
      }
    } else {
      await handleResetRequest();
    }
  };

  const handleResendVerification = async () => {
    if (!form.email) {
      setResendMessage("Please enter your email above to resend the confirmation.");
      return;
    }
    setResendSending(true);
    setResendMessage(null);
    try {
      const res = await AuthService.post(endpoints.VERIFY_EMAIL_RESEND, { email: form.email });
      setResendMessage(res?.data?.detail || "Verification link sent.");
    } catch {
      setResendMessage("Unable to send verification email. Please try again.");
    } finally {
      setResendSending(false);
    }
  };

  const handleResetRequest = async () => {
    if (!resetEmail) {
      setResetMessage("Please enter your email.");
      return;
    }
    try {
      await AuthService.post(endpoints.PASSWORD_RESET, { email: resetEmail });
      setResetMessage("If that email exists, a reset link has been sent.");
      setResetSubmitted(true);
    } catch {
      setResetMessage("Unable to send reset link. Please try again.");
    }
  };

  const closeDialog = () => {
    resetState();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={closeDialog}
      maxWidth="xs"
      fullWidth
      BackdropProps={{ sx: { backgroundColor: "rgba(0,0,0,0.25)" } }}
      PaperProps={{
        sx: {
          backgroundColor: "#fff",
          "&:hover": { backgroundColor: "#fff" },
        },
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>{mode === "login" ? "Sign In" : "Reset Password"}</DialogTitle>
        <DialogContent sx={{ mt: 1 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {mode === "login" && error && (
              <Alert
                severity="error"
                action={
                  errorKind === "unverified" ? (
                    <Button
                      color="inherit"
                      size="small"
                      onClick={handleResendVerification}
                      disabled={resendSending}
                    >
                      {resendSending ? "Sending..." : "Resend"}
                    </Button>
                  ) : null
                }
              >
                {error}
              </Alert>
            )}
            {mode === "login" && resendMessage && (
              <Typography color="text.secondary">{resendMessage}</Typography>
            )}
            {mode === "reset" && resetMessage && (
              <Typography color={resetSubmitted ? "success.main" : "error"}>{resetMessage}</Typography>
            )}
            {mode === "login" ? (
              <>
                <TextField
                  label="Email"
                  name="email"
                  fullWidth
                  onChange={handleChange}
                  autoComplete="email"
                  autoFocus
                />
                <TextField
                  label="Password"
                  name="password"
                  type="password"
                  fullWidth
                  onChange={handleChange}
                  autoComplete="current-password"
                />
              </>
            ) : (
              <>
                <Typography variant="body2">
                  Enter your account email and we'll send you a reset link.
                </Typography>
                <TextField
                  label="Email"
                  type="email"
                  fullWidth
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  autoComplete="email"
                  autoFocus
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          {mode === "login" ? (
            <>
              {errorKind !== "unverified" && (
                <Button
                  onClick={() => {
                    setMode("reset");
                    setResetMessage(null);
                    setResetSubmitted(false);
                  }}
                >
                  Forgot password?
                </Button>
              )}
              <Box sx={{ flex: 1 }} />
              <Button onClick={closeDialog}>Cancel</Button>
              <Button variant="contained" type="submit">
                Sign In
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={() => {
                  if (resetSubmitted) {
                    closeDialog();
                  } else {
                    setMode("login");
                    setResetMessage(null);
                    setResetSubmitted(false);
                  }
                }}
              >
                {resetSubmitted ? "Close" : "Back to login"}
              </Button>
              <Button variant="contained" onClick={handleResetRequest}>
                Send Reset Link
              </Button>
            </>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
}
